import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { blastByNameQueryOptions } from '~api/queries';
import type { Blast, BlastJobStatus } from '~api/services';
import { optimiserService } from '~api/services';
import { trackEvent } from '~libs';

/* -------------------------------------------------------------------------- */
/*                                    Types                                   */
/* -------------------------------------------------------------------------- */

/**
 * Discriminated union representing the current state of a blast's optimisation job.
 * Mutually exclusive by construction — consumers cannot render conflicting states.
 */
type OptimisationsJobState =
  | { state: 'idle' }
  | { state: 'running' }
  | { state: 'triggering' }
  | { state: 'cannot-optimise'; reasons: string[] }
  | { state: 'failed'; reasons: string[] }
  | { state: 'trigger-error'; error: Error | null };

type DeriveJobStateInput = {
  isTriggerBuildError: boolean;
  triggerBuildError: Error | null;
  jobStatus: BlastJobStatus | null;
  reasons: string[];
  isTriggeringBuild: boolean;
};

/* -------------------------------------------------------------------------- */
/*                                  Constants                                 */
/* -------------------------------------------------------------------------- */

// 5 minutes in milliseconds
const POLLING_INTERVAL_MS = 5 * 60 * 1000;

const ACTIVE_STATUSES = new Set<BlastJobStatus>(['pending', 'running']);

/* -------------------------------------------------------------------------- */
/*                                   Helpers                                  */
/* -------------------------------------------------------------------------- */

/**
 * Derives the mutually-exclusive job state from raw hook signals.
 * Priority order matters: trigger error surface → terminal state → in-progress → idle.
 */
const deriveJobState = ({
  isTriggerBuildError,
  triggerBuildError,
  jobStatus,
  reasons,
  isTriggeringBuild,
}: DeriveJobStateInput): OptimisationsJobState => {
  if (isTriggerBuildError) return { state: 'trigger-error', error: triggerBuildError };
  if (isTriggeringBuild) return { state: 'triggering' };
  if (jobStatus === 'cannot_optimise') return { state: 'cannot-optimise', reasons };
  if (jobStatus === 'failed') return { state: 'failed', reasons };
  if (jobStatus === 'pending' || jobStatus === 'running') return { state: 'running' };
  return { state: 'idle' };
};

/* -------------------------------------------------------------------------- */
/*                                  Main hook                                 */
/* -------------------------------------------------------------------------- */

/**
 * Manages the build optimisation job lifecycle for a single blast:
 * - triggers the build via mutation
 * - polls blast status until the job completes
 * - returns derived status for the UI
 */
function useBlastOptimisationJob(blast: Blast) {
  const queryClient = useQueryClient();

  // Tracks whether the user triggered a build — stays true until the first poll
  // response arrives, bridging the gap between mutation completing and polling starting.
  const [hasTriggered, setHasTriggered] = useState(false);

  // Always reflects current blast prop AND user trigger
  const pollingEnabled = hasTriggered || ACTIVE_STATUSES.has(blast.latest_optimisation_job?.status as BlastJobStatus);

  const { data: liveBlast } = useQuery({
    ...blastByNameQueryOptions(blast.name),
    enabled: pollingEnabled,
    refetchInterval: (query) => {
      const status = query.state.data?.latest_optimisation_job?.status;
      return status && ACTIVE_STATUSES.has(status) ? POLLING_INTERVAL_MS : false;
    },
  });

  const {
    mutate: triggerBuild,
    isPending: isMutationPending,
    isError: isTriggerBuildError,
    error: triggerBuildError,
  } = useMutation({
    mutationFn: () => optimiserService.triggerOptimisationBuild(blast.name),
    onSuccess: () => {
      setHasTriggered(true);
      trackEvent('optimisation_build_triggered', { blastName: blast.name });
      // Immediately fetch fresh status rather than waiting for the next interval
      queryClient.invalidateQueries({ queryKey: ['blasts', 'detail', blast.name] });
    },
    onError: (error) => {
      setHasTriggered(false);
      trackEvent('optimisation_build_failed', {
        blastName: blast.name,
        errorMessage: error.message,
      });
    },
  });

  // Prefer live polled data; fall back to the prop (initial render)
  const effectiveJob = (liveBlast ?? blast).latest_optimisation_job;
  const jobStatus = effectiveJob?.status ?? null;

  // True during mutation AND during the window between mutation completing and
  // the first poll response arriving.
  const isTriggeringBuild = isMutationPending || (hasTriggered && !liveBlast);

  const jobState = deriveJobState({
    isTriggerBuildError,
    triggerBuildError: triggerBuildError ?? null,
    jobStatus,
    reasons: effectiveJob?.messages ?? [],
    isTriggeringBuild,
  });

  return { jobStatus, jobState, triggerBuild };
}

export type { OptimisationsJobState };
export { useBlastOptimisationJob };
